
import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import AccessibleBreadcrumb from '@/components/navigation/AccessibleBreadcrumb';
import { generateEnhancedSchema, generateBreadcrumbData } from '@/utils/accessibleSEO';

const TermsPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  const contentRef = useRef<HTMLDivElement>(null);
  
  // References for each section for smooth scrolling
  const section1Ref = useRef<HTMLHeadingElement>(null);
  const section2Ref = useRef<HTMLHeadingElement>(null);
  const section3Ref = useRef<HTMLHeadingElement>(null);
  const section4Ref = useRef<HTMLHeadingElement>(null);
  const section5Ref = useRef<HTMLHeadingElement>(null);
  const section6Ref = useRef<HTMLHeadingElement>(null);
  const section7Ref = useRef<HTMLHeadingElement>(null);
  const section8Ref = useRef<HTMLHeadingElement>(null);
  const section9Ref = useRef<HTMLHeadingElement>(null);
  const section10Ref = useRef<HTMLHeadingElement>(null);
  const section11Ref = useRef<HTMLHeadingElement>(null);
  const section12Ref = useRef<HTMLHeadingElement>(null);
  const section13Ref = useRef<HTMLHeadingElement>(null);
  const section14Ref = useRef<HTMLHeadingElement>(null);
  
  // Function for smooth scrolling to sections
  const scrollToSection = (ref: React.RefObject<HTMLHeadingElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  useEffect(() => {
    // Update SEO meta tags based on current language
    const metaTitles = {
      de: 'Allgemeine Geschäftsbedingungen (AGB) | Future Level',
      en: 'Terms and Conditions | Future Level',
      fr: 'Conditions Générales | Future Level',
      it: 'Termini e Condizioni | Future Level'
    };
    
    const metaDescriptions = {
      de: 'Allgemeine Geschäftsbedingungen (AGB) der Future Level - KI-basierte Videomarketing Agentur in Emmen, Schweiz',
      en: 'Terms and Conditions of Future Level - AI-based video marketing agency in Emmen, Switzerland',
      fr: 'Conditions Générales de Future Level - Agence de marketing vidéo basée sur l\'IA à Emmen, Suisse',
      it: 'Termini e Condizioni di Future Level - Agenzia di marketing video basata sull\'IA a Emmen, Svizzera'
    };
    
    const pagePath = currentLanguage === 'de' ? '/agb' : 
                    currentLanguage === 'en' ? '/terms' : 
                    currentLanguage === 'fr' ? '/conditions' : '/termini';
    
    const canonicalUrl = getCanonicalUrl(pagePath.substring(1), currentLanguage);
    
    updateMetaTags({
      title: metaTitles[currentLanguage],
      description: metaDescriptions[currentLanguage],
      keywords: "AGB, Geschäftsbedingungen, Terms of Service, Future Level, Videoproduktion Bedingungen, Schweizer AGB",
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "article"
    });
    
    // Generate schema markup for the legal content
    const breadcrumbItems = generateBreadcrumbData(
      pagePath,
      currentLanguage === 'de' ? 'AGB' : 
      currentLanguage === 'en' ? 'Terms and Conditions' :
      currentLanguage === 'fr' ? 'Conditions Générales' : 'Termini e Condizioni',
      currentLanguage
    );
    
    generateEnhancedSchema({
      title: metaTitles[currentLanguage],
      description: metaDescriptions[currentLanguage],
      type: 'Article',
      publishDate: '2025-05-01',
      modifiedDate: '2025-05-10',
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      keywords: "AGB, Terms, Conditions, Legal, Future Level, Video Marketing, AI",
      breadcrumbItems: breadcrumbItems
    });
    
  }, [currentLanguage, t]);

  // Define breadcrumb items based on current language
  const breadcrumbItems = [
    {
      label: currentLanguage === 'de' ? 'AGB' : 
             currentLanguage === 'en' ? 'Terms and Conditions' :
             currentLanguage === 'fr' ? 'Conditions Générales' : 'Termini e Condizioni'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main id="main-content" className="flex-grow py-8 md:py-16">
        <div className="container mx-auto px-4">
          <AccessibleBreadcrumb items={breadcrumbItems} className="mb-8" />
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-8 text-gray-200">{t.terms}</h1>
            
            {/* Table of Contents - with accessibility improvements */}
            <nav aria-labelledby="toc-heading" className="bg-black/20 backdrop-blur-sm p-6 rounded-lg mb-8 border border-purple-500/20 shadow-md">
              <h2 id="toc-heading" className="text-xl font-semibold mb-4 flex items-center text-gray-200">
                {currentLanguage === 'de' ? 'Inhaltsverzeichnis' : 
                currentLanguage === 'en' ? 'Table of Contents' :
                currentLanguage === 'fr' ? 'Table des matières' : 'Indice'}
                <ChevronDown className="ml-2 h-5 w-5" aria-hidden="true" />
              </h2>
              <ol className="space-y-2 list-decimal list-inside text-gray-200">
                <li>
                  <button 
                    onClick={() => scrollToSection(section1Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Allgemeine Geschäftsbedingungen (AGB)
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section2Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Unternehmensdetails gemäß schweizerischem Recht
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section3Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Vertragsabschluss
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section4Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Leistungsumfang
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section5Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Preise und Zahlungsbedingungen
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section6Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Nutzungsrechte
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section7Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Haftungsbeschränkung
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section8Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Datenschutz
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section9Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Geheimhaltung
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section10Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Bestellprozess für E-Commerce
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section11Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Barrierefreiheit
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section12Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Anwendbares Recht und Gerichtsstand
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section13Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Änderungen der AGB
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection(section14Ref)}
                    className="text-left hover:text-purple-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  >
                    Schlussbestimmungen
                  </button>
                </li>
              </ol>
            </nav>
            
            {/* Improved semantic structure and styling for terms content */}
            <div ref={contentRef} className="prose prose-invert max-w-none bg-black/20 backdrop-blur-sm p-8 rounded-lg border border-purple-500/20 shadow-md">
              <section id="section-1" aria-labelledby="section-title-1">
                <h2 id="section-title-1" ref={section1Ref} className="text-2xl font-bold mb-4 text-purple-400 scroll-mt-24">1. Allgemeine Geschäftsbedingungen (AGB)</h2>
                <p className="mb-4 text-gray-200">
                  Diese Allgemeinen Geschäftsbedingungen regeln das Vertragsverhältnis zwischen 
                  der Future Level (nachfolgend "Anbieter") und ihren Kunden (nachfolgend "Kunde") 
                  für sämtliche angebotenen Dienstleistungen im Bereich des KI-gestützten Videomarketings.
                </p>
              </section>
              
              <section id="section-2" aria-labelledby="section-title-2">
                <h2 id="section-title-2" ref={section2Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">2. Unternehmensdetails gemäß schweizerischem Recht</h2>
                <div className="bg-black/30 p-6 rounded-md border-l-4 border-purple-500">
                  <p className="mb-1 text-gray-200"><strong className="text-purple-300">Unternehmensname:</strong> Future Level</p>
                  <p className="mb-1 text-gray-200"><strong className="text-purple-300">Adresse:</strong> Zumhofstrasse, 6010 Kriens, Schweiz</p>
                  <p className="mb-1 text-gray-200"><strong className="text-purple-300">E-Mail:</strong> info@future-level.ch</p>
                  <p className="mb-0 text-gray-200"><strong className="text-purple-300">Telefon:</strong> +41 41 539 16 13</p>
                </div>
              </section>
              
              <section id="section-3" aria-labelledby="section-title-3">
                <h2 id="section-title-3" ref={section3Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">3. Vertragsabschluss</h2>
                <p className="mb-4 text-gray-200">
                  Der Vertrag zwischen dem Anbieter und dem Kunden kommt durch die schriftliche Bestätigung 
                  eines Angebots oder Auftrags zustande. Als schriftliche Bestätigung gilt auch die Bestätigung 
                  per E-Mail oder andere elektronische Kommunikationswege.
                </p>
              </section>
              
              <section id="section-4" aria-labelledby="section-title-4">
                <h2 id="section-title-4" ref={section4Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">4. Leistungsumfang</h2>
                <p className="mb-4 text-gray-200">
                  Der genaue Leistungsumfang wird im individuellen Angebot oder in der Auftragsbestätigung 
                  festgehalten. Der Anbieter behält sich das Recht vor, zur Erfüllung seiner vertraglichen 
                  Pflichten Dritte beizuziehen.
                </p>
              </section>
              
              <section id="section-5" aria-labelledby="section-title-5">
                <h2 id="section-title-5" ref={section5Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">5. Preise und Zahlungsbedingungen</h2>
                <p className="mb-4 text-gray-200">
                  Alle Preise verstehen sich in Schweizer Franken (CHF) und zuzüglich der gesetzlichen 
                  Mehrwertsteuer. Rechnungen sind innerhalb von 30 Tagen nach Erhalt ohne Abzug zahlbar, 
                  sofern nicht anders vereinbart.
                </p>
              </section>
              
              <section id="section-6" aria-labelledby="section-title-6">
                <h2 id="section-title-6" ref={section6Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">6. Nutzungsrechte</h2>
                <p className="mb-4 text-gray-200">
                  Mit vollständiger Bezahlung der vereinbarten Vergütung erhält der Kunde das Recht, 
                  die erstellten Videoinhalte im vereinbarten Umfang zu nutzen. Die genauen Nutzungsrechte 
                  werden im Einzelfall festgelegt.
                </p>
              </section>
              
              <section id="section-7" aria-labelledby="section-title-7">
                <h2 id="section-title-7" ref={section7Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">7. Haftungsbeschränkung</h2>
                <p className="mb-4 text-gray-200">
                  Der Anbieter haftet für Schäden nur bei Vorsatz oder grober Fahrlässigkeit. 
                  Die Haftung für leichte Fahrlässigkeit ist ausgeschlossen. Die Haftung für indirekte 
                  Schäden und Folgeschäden ist in jedem Fall ausgeschlossen, soweit dies gesetzlich zulässig ist.
                </p>
              </section>
              
              <section id="section-8" aria-labelledby="section-title-8">
                <h2 id="section-title-8" ref={section8Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">8. Datenschutz</h2>
                <p className="mb-4 text-gray-200">
                  Der Anbieter verpflichtet sich zur Einhaltung der geltenden datenschutzrechtlichen 
                  Bestimmungen, insbesondere des Bundesgesetzes über den Datenschutz (DSG). 
                  Weitere Informationen sind in der separaten <a href={`/${currentLanguage}/${currentLanguage === 'de' ? 'datenschutz' : currentLanguage === 'en' ? 'privacy-policy' : currentLanguage === 'fr' ? 'politique-de-confidentialite' : 'informativa-privacy'}`} className="text-purple-400 hover:text-purple-300 underline focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded">Datenschutzerklärung</a> enthalten.
                </p>
              </section>
              
              <section id="section-9" aria-labelledby="section-title-9">
                <h2 id="section-title-9" ref={section9Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">9. Geheimhaltung</h2>
                <p className="mb-4 text-gray-200">
                  Beide Parteien verpflichten sich, alle vertraulichen Informationen, die ihnen im Rahmen 
                  der Zusammenarbeit zugänglich werden, streng vertraulich zu behandeln und nicht an Dritte 
                  weiterzugeben.
                </p>
              </section>
              
              <section id="section-10" aria-labelledby="section-title-10">
                <h2 id="section-title-10" ref={section10Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">10. Bestellprozess für E-Commerce</h2>
                <p className="mb-4 text-gray-200">
                  Bei Online-Bestellungen werden Kunden vor Abschluss der Bestellung über alle relevanten 
                  Schritte informiert. Der Kunde erhält die Möglichkeit, seine Bestellung zu überprüfen und 
                  Fehler zu korrigieren, bevor er eine verbindliche Bestellung abgibt. Nach Eingang der Bestellung 
                  erhält der Kunde unverzüglich eine elektronische Bestätigung.
                </p>
              </section>
              
              <section id="section-11" aria-labelledby="section-title-11">
                <h2 id="section-title-11" ref={section11Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">11. Barrierefreiheit</h2>
                <div className="bg-black/30 p-6 rounded-md border-l-4 border-purple-500">
                  <p className="mb-0 text-gray-200">
                    Future Level bemüht sich, seine Online-Präsenz barrierefrei zu gestalten und orientiert sich 
                    dabei an den WCAG 2.1 Level AA Standards, um die Zugänglichkeit für Personen mit Behinderungen 
                    zu gewährleisten.
                  </p>
                </div>
              </section>
              
              <section id="section-12" aria-labelledby="section-title-12">
                <h2 id="section-title-12" ref={section12Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">12. Anwendbares Recht und Gerichtsstand</h2>
                <p className="mb-4 text-gray-200">
                  Diese AGB und alle Rechtsbeziehungen zwischen dem Anbieter und dem Kunden unterliegen 
                  ausschließlich schweizerischem Recht unter Ausschluss des UN-Kaufrechts. Gerichtsstand für 
                  alle Streitigkeiten aus oder im Zusammenhang mit diesen AGB ist Emmen, Schweiz.
                </p>
              </section>
              
              <section id="section-13" aria-labelledby="section-title-13">
                <h2 id="section-title-13" ref={section13Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">13. Änderungen der AGB</h2>
                <p className="mb-4 text-gray-200">
                  Der Anbieter behält sich das Recht vor, diese AGB jederzeit anzupassen. Die jeweils aktuelle 
                  Version wird auf der Website veröffentlicht. Es gilt die zum Zeitpunkt des Vertragsabschlusses 
                  gültige Version der AGB.
                </p>
              </section>
              
              <section id="section-14" aria-labelledby="section-title-14">
                <h2 id="section-title-14" ref={section14Ref} className="text-2xl font-bold mt-8 mb-4 text-purple-400 scroll-mt-24">14. Schlussbestimmungen</h2>
                <p className="mb-4 text-gray-200">
                  Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, so wird die Gültigkeit der 
                  übrigen Bestimmungen davon nicht berührt. Anstelle der unwirksamen Bestimmung gilt eine 
                  wirksame Bestimmung als vereinbart, die der von den Parteien gewollten wirtschaftlich am 
                  nächsten kommt.
                </p>
              </section>
              
              <footer className="mt-12 pt-6 border-t border-gray-700 text-sm text-gray-400 flex items-center justify-between">
                <p>Stand: Mai 2025</p>
                <a 
                  href="#toc-heading" 
                  className="flex items-center text-purple-400 hover:text-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Zurück zum Anfang
                </a>
              </footer>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;
